// Imports
import { DateTime } from 'luxon';


// Interfaces
interface FormatDateRangeOptions {
	start: Date;
	end?: Date;
	allDay?: boolean;
	shortenMonth?: boolean;
	dateFormat: 'MDY' | 'DMY';
	timeFormat: '12HR' | '24HR' | '24HR_NO_COLON';
	timezone?: string;
}


/** Intelligently formats a range of date times to a string to match given style parameters */
export default function formatDateRange(options: FormatDateRangeOptions) {
	// Initialize
	const start = DateTime.fromJSDate(options.start, { zone: options.timezone });
	const end = options.end ? DateTime.fromJSDate(options.end, { zone: options.timezone }) : null;
	const sameDate = end === null || start.startOf('day').equals(end.startOf('day'));
	const dateLocale = options.dateFormat === 'MDY' ? 'en-US' : 'en-GB';
	const timeLocale = options.timeFormat === '12HR' ? 'en-US' : 'en-GB';
	const colonReplacement = options.timeFormat === '24HR_NO_COLON' ? '' : ':';
	
	
	// Build formatting options
	const dateFormatOptions = {
		year: 'numeric',
		month: options.shortenMonth ? 'short' : 'long',
		day: 'numeric',
		timeZone: options.timezone,
	} as const;
	
	const determineTimeFormatOptions = (minute: number) =>
		({
			hour: 'numeric',
			minute: options.timeFormat !== '12HR' || minute !== 0 ? 'numeric' : undefined,
		}) as const;
	
	
	// Handle all day events
	if (options.allDay) {
		// Initialize
		const dateTimeFormat = new Intl.DateTimeFormat(dateLocale, dateFormatOptions);
		
		
		// Format and return date range
		//  -> Held in a `string`-typed local so the `any` introduced by the `@ts-ignore` below can't leak into the inferred return type
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
		const formattedDateRange: string = dateTimeFormat
			// LATER: Remove `@ts-ignore` and `eslint-disable-next-line` once our tsconfig lib includes the ES2021 Intl typings that type `Intl.DateTimeFormat.formatRange()`
			// @ts-ignore: `Intl.DateTimeFormat.formatRange()` is absent from our ES2019 lib (typed only in `es2021.intl`)
			.formatRange(start.toJSDate(), end?.toJSDate() ?? start.toJSDate());
		
		return formattedDateRange;
	}
	
	
	// Format start date and time
	const startDate = start.toLocaleString(dateFormatOptions, { locale: dateLocale });
	const startTime = start
		.toLocaleString(determineTimeFormatOptions(start.minute), { locale: timeLocale })
		.replace(':', colonReplacement);
	
	const formattedStart = `${startDate}, ${startTime}`;
	
	
	// Return only the start if there's no distinct end
	if (!end || start.equals(end)) {
		return formattedStart;
	}
	
	
	// Format end time, preceded by the end date if it differs
	const endDate = end.toLocaleString(dateFormatOptions, { locale: dateLocale });
	const endTime = end
		.toLocaleString(determineTimeFormatOptions(end.minute), { locale: timeLocale })
		.replace(':', colonReplacement);
	
	const formattedEnd = sameDate ? endTime : `${endDate}, ${endTime}`;
	
	
	// Join with the thin-spaced en dash that `Intl.DateTimeFormat.formatRange()` uses, and return
	const thinSpace = '\u2009';
	const enDash = '–';
	const rangeSeparator = `${thinSpace}${enDash}${thinSpace}`;
	
	return `${formattedStart}${rangeSeparator}${formattedEnd}`;
}
